import { ReactNode } from "react"
import { ProjectHealth, ProjectHealthUI } from "models/project-health"
import { formatCurrency, formatNumber } from "utils/tools"

type Tone = "good" | "warning" | "bad" | "neutral"

const TONE_ICON: Record<Tone, string> = {
    good: "mdi-check-circle",
    warning: "mdi-alert",
    bad: "mdi-alert-octagon",
    neutral: "mdi-minus-circle-outline",
}

const TONE_LABEL: Record<Tone, string> = { good: "On track", warning: "Watch", bad: "Action needed", neutral: "No data" }

const money = (v: number | null | undefined) => formatCurrency(v ?? 0, "CAD", 0)
const pct = (v: number) => `${formatNumber(v, 1)}%`
const clamp = (v: number, max = 100) => Math.max(0, Math.min(max, v))

interface TileProps {
    icon: string
    label: string
    tone: Tone
    value: ReactNode
    children?: ReactNode
}

// One key figure: icon + title, big value, a visual (bar) and a short caption. The tone sets the left
// border and a small status icon + label, so status is never shown by colour alone.
const Tile = ({ icon, label, tone, value, children }: TileProps) => (
    <div className={`kpi kpi-tone-${tone}`}>
        <div className="kpi-head">
            <label><i className={`mdi ${icon} me-1`} aria-hidden />{label}</label>
            <span className={`kpi-tone-label tone-${tone}`} title={TONE_LABEL[tone]}>
                <i className={`mdi ${TONE_ICON[tone]}`} aria-hidden /> <span className="visually-hidden">{TONE_LABEL[tone]}</span>
            </span>
        </div>
        <strong>{value}</strong>
        {children}
    </div>
)

// Horizontal bar made of parts (each a % of the whole) with an optional legend
const StackBar = ({ parts }: { parts: { value: number, className: string, label: string }[] }) => (
    <div className="kpi-stack" role="img" aria-label={parts.map(p => `${p.label} ${formatNumber(p.value, 0)}%`).join(", ")}>
        {parts.map(p => <div key={p.label} className={p.className} style={{ width: `${clamp(p.value)}%` }} title={`${p.label}: ${formatNumber(p.value, 1)}%`} />)}
    </div>
)

// Gross margin on its bands: red below warning, yellow up to the minimum, green above; marker = actual
const MarginBands = ({ value, warning, minimum }: { value: number, warning: number, minimum: number }) => {
    const scale = Math.max(minimum + 25, value + 5, 60)
    const pos = (v: number) => `${clamp((v / scale) * 100)}%`
    return (
        <div className="kpi-bands" role="img" aria-label={`Margin ${formatNumber(value, 1)}%; warning from ${warning}%, minimum ${minimum}%`}>
            <div className="band band-bad" style={{ width: pos(warning) }} />
            <div className="band band-warning" style={{ width: `calc(${pos(minimum)} - ${pos(warning)})` }} />
            <div className="band band-good" style={{ flex: 1 }} />
            <div className="marker" style={{ left: pos(value) }} />
        </div>
    )
}

const toneFromMargin = (m: number | null, warning: number, minimum: number): Tone =>
    m === null ? "neutral" : m >= minimum ? "good" : m >= warning ? "warning" : "bad"

const HealthKeyFigures = ({ health }: { health: ProjectHealth }) => {
    const { kpis, config } = health
    const unbilledMax = config?.UNBILLED_MAX_DAYS ?? 45

    const costShare = kpis.revenue > 0 ? (kpis.cost / kpis.revenue) * 100 : null
    const billedShare = kpis.revenue > 0 ? (kpis.billed / kpis.revenue) * 100 : null
    const earned = kpis.incomeBudget > 0 ? (kpis.revenue / kpis.incomeBudget) * 100 : null
    const projectedShare = kpis.incomeBudget > 0 ? (kpis.projectedRevenue / kpis.incomeBudget) * 100 : null
    const costUsed = kpis.expenseBudget > 0 ? (kpis.cost / kpis.expenseBudget) * 100 : null
    const monthDelta = kpis.averageMonthlyRevenue > 0
        ? ((kpis.lastMonthRevenue - kpis.averageMonthlyRevenue) / kpis.averageMonthlyRevenue) * 100
        : null
    const days = kpis.lastBilledDays

    const billedTone: Tone = days === null ? (kpis.accrued > 0 ? "bad" : "neutral")
        : days <= 30 ? "good" : days <= unbilledMax ? "warning" : "bad"

    return (
        <div className="health-kpis">
            <Tile icon="mdi-percent-circle-outline" label="Gross Margin (LTD)"
                tone={toneFromMargin(kpis.marginPercent, kpis.marginWarning, kpis.marginMinimum)}
                value={kpis.marginPercent !== null ? pct(kpis.marginPercent) : "—"}>
                {kpis.marginPercent !== null && <MarginBands value={kpis.marginPercent} warning={kpis.marginWarning} minimum={kpis.marginMinimum} />}
                <small>
                    Minimum {kpis.marginMinimum}% · warning {kpis.marginWarning}%
                    {kpis.marginSource === "FY" ? ` (FY${kpis.marginFiscalYear})` : " (default)"}
                </small>
            </Tile>

            <Tile icon="mdi-cash-multiple" label="Revenue (LTD)" tone={costShare === null ? "neutral" : costShare <= 100 ? "good" : "bad"}
                value={money(kpis.revenue)}>
                {costShare !== null && <StackBar parts={[
                    { value: costShare, className: "part-cost", label: "Cost" },
                    { value: 100 - costShare, className: "part-margin", label: "Margin" },
                ]} />}
                <small>Cost {money(kpis.cost)}{costShare !== null ? ` (${formatNumber(costShare, 0)}% of revenue)` : ""}</small>
            </Tile>

            <Tile icon="mdi-file-document-check-outline" label="Billed vs Accrued"
                tone={billedShare === null ? "neutral" : kpis.accrued <= 0 ? "good" : billedShare >= 80 ? "good" : "warning"}
                value={money(kpis.billed)}>
                {billedShare !== null && <StackBar parts={[
                    { value: billedShare, className: "part-billed", label: "Billed" },
                    { value: 100 - billedShare, className: "part-accrued", label: "Accrued" },
                ]} />}
                <small>
                    <span className="legend-dot part-billed" /> Billed {billedShare !== null ? `${formatNumber(billedShare, 0)}%` : "—"}
                    {" · "}
                    <span className="legend-dot part-accrued" /> {money(kpis.accrued)} accrued
                </small>
            </Tile>

            <Tile icon="mdi-clock-outline" label="Last Billed" tone={billedTone}
                value={ProjectHealthUI.periodLabel(kpis.lastBilledPeriod)}>
                <small>
                    {days === null ? "Nothing billed yet" : `~${days} days ago`} · alert after {unbilledMax} days
                </small>
            </Tile>

            <Tile icon="mdi-calendar-month-outline" label="Last Month Revenue"
                tone={monthDelta === null ? "neutral" : monthDelta >= -10 ? "good" : monthDelta >= -30 ? "warning" : "bad"}
                value={money(kpis.lastMonthRevenue)}>
                <small>
                    {monthDelta !== null && (
                        <span className={monthDelta >= 0 ? "text-success" : "text-danger"}>
                            <i className={`mdi ${monthDelta >= 0 ? "mdi-arrow-up-bold" : "mdi-arrow-down-bold"}`} aria-hidden />
                            {formatNumber(Math.abs(monthDelta), 0)}% {monthDelta >= 0 ? "above" : "below"}{" "}
                        </span>
                    )}
                    6-month avg {money(kpis.averageMonthlyRevenue)}
                </small>
            </Tile>

            <Tile icon="mdi-briefcase-outline" label="Income Budget"
                tone={earned === null ? "neutral" : earned > 100 ? "bad" : "good"}
                value={money(kpis.incomeBudget)}>
                {earned !== null && <div className="progress kpi-progress">
                    <div className={`progress-bar ${earned > 100 ? "bg-danger" : "bg-primary"}`} style={{ width: `${clamp(earned)}%` }} />
                </div>}
                <small>{earned !== null ? `${pct(earned)} earned · ` : ""}{money(kpis.budgetRemaining)} remaining</small>
            </Tile>

            <Tile icon="mdi-chart-line" label="Actual + Forecast"
                tone={projectedShare === null ? "neutral" : projectedShare > 100 ? "bad" : projectedShare > 95 ? "warning" : "good"}
                value={money(kpis.projectedRevenue)}>
                {projectedShare !== null && <div className="progress kpi-progress">
                    <div className={`progress-bar ${projectedShare > 100 ? "bg-danger" : projectedShare > 95 ? "bg-warning" : "bg-success"}`}
                        style={{ width: `${clamp(projectedShare)}%` }} />
                </div>}
                <small>
                    {kpis.incomeBudget > 0
                        ? `${pct(projectedShare ?? 0)} of budget · ${kpis.projectedRevenue > kpis.incomeBudget ? "over" : "under"} by ${money(Math.abs(kpis.projectedRevenue - kpis.incomeBudget))}`
                        : "No Income budget"}
                </small>
            </Tile>

            <Tile icon="mdi-cart-outline" label="Cost vs Expense Budget"
                tone={costUsed === null ? "neutral" : costUsed > 100 ? "bad" : costUsed >= 75 ? "warning" : "good"}
                value={money(kpis.cost)}>
                {costUsed !== null && <div className="progress kpi-progress">
                    <div className={`progress-bar ${costUsed > 100 ? "bg-danger" : costUsed >= 75 ? "bg-warning" : "bg-success"}`}
                        style={{ width: `${clamp(costUsed)}%` }} />
                </div>}
                <small>{costUsed !== null ? `${pct(costUsed)} of ${money(kpis.expenseBudget)}` : "No Expense budget"}</small>
            </Tile>
        </div>
    )
}

export default HealthKeyFigures
